import React from 'react';
import { Trophy, Award, Handshake, Download, Star, Users, Quote, Gavel } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import ReportViewer from '../components/ReportViewer';
import LeadForm from '../components/LeadForm';
import { useContent } from '../context/ContentContext';

const PURPLE = '#5B2A86';
const DEEP = '#3B1A5C';
const GOLD = '#C9A24B';
const REPORT = 'https://customer-assets-lqy194kg.emergentagent.net/job_enersider-revamp/artifacts/ep4qpar5_Postshow%20Report_FELAS.pdf';
const HERO = 'https://images.unsplash.com/photo-1650240852447-46505dba4726?crop=entropy&cs=srgb&fm=jpg&q=85';

const CATEGORIES = ['NextGen Leader', 'SHEnergy Leader', 'Emerging Energy Enterprise', 'NumerUno Projects', 'EnergyIcon', 'PSUs Driving Viksit Bharat', 'Technovators Team'];
const SECTORS = ['Oil', 'Gas', 'Solar', 'Wind', 'GH2', 'Bio', 'Nuclear', 'Hydro', 'Thermal', 'Climate', 'Water×Energy', 'Power', 'BESS', 'EVs', 'Infra×Energy'];

const jury = [
  ['Ajay Mathur', 'Jury Chair'], ['D V Shastry', 'Jury Member'], ['Bhaskar Chatterjee', 'Jury Member'],
  ['Shishir Priyadarshi', 'Jury Member'], ['Gaurav Kedia', 'Jury Member'], ['Vibhuti Garg', 'Jury Member'],
  ['Subrahmanyam Pulipaka', 'Jury Member'], ['Vaibhav Dange', 'Jury Member'],
  ['Nishaanth Balashanmugam', 'Jury Member'], ['Netra Walawalkar', 'Jury Member'],
];
const honourees = [
  ['Anirudh Saraswat', 'Oriana Power', 'NextGen Leader'],
  ['Sindoor Mittal', 'Avaada Group', 'SHEnergy Leader'],
  ['Rashi Gupta', 'Vision Mechatronics', 'SHEnergy Leader'],
  ['Hero Future Energies', '', 'Technovators'],
  ['Indian Gas Exchange (IGX)', '', 'Technovators'],
  ['Husk Power Systems', '', 'Technovators'],
  ['Advait Greenergy', '', 'Numero Uno'],
  ['Maharashtra Natural Gas Ltd', '', 'Numero Uno'],
];

export default function FELAS() {
  const { eventsData } = useContent();
  const hero = eventsData?.felas?.image || HERO;
  return (
    <div className="pb-24">
      {/* Hero */}
      <section className="section-pad pt-28 md:pt-36">
        <div className="max-w-[1500px] mx-auto">
          <div className="relative rounded-3xl overflow-hidden text-white">
            <img src={hero} alt="FELAS" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${DEEP}f5, ${PURPLE}dd 55%, ${DEEP}cc)` }} />
            <div className="relative p-10 md:p-16">
              <Reveal>
                <p className="eyebrow mb-3" style={{ color: GOLD }}>FELAS</p>
                <h1 className="font-display text-5xl md:text-7xl leading-[0.98]">Future Energy Leaders Award Show</h1>
                <p className="text-white/85 text-lg mt-5 max-w-2xl">India's biggest energy-sector award show — a glittering evening honouring the organisations and individuals shaping the nation's energy future across seven categories and fifteen sectors.</p>
                <div className="flex flex-wrap gap-3 mt-8">
                  <a href="#nominate" className="inline-flex items-center gap-2 font-semibold px-6 py-3.5 rounded-full" style={{ background: GOLD, color: DEEP }}><Trophy size={18} /> Submit a nomination</a>
                  <a href="#partnership" data-testid="felas-partnership-cta" className="inline-flex items-center gap-2 border border-white/40 text-white font-medium px-6 py-3.5 rounded-full hover:bg-white/10"><Handshake size={18} /> Partnership</a>
                  <a href="#felas-report" className="inline-flex items-center gap-2 border border-white/40 text-white font-medium px-6 py-3.5 rounded-full hover:bg-white/10"><Download size={18} /> Post-show Report</a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-pad py-14">
        <div className="max-w-[1500px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[['200+', 'Nominations'], ['100+', 'CXOs'], ['50+', 'Awardees'], ['15', 'Sectors']].map(([v, l]) => (
            <div key={l} className="border-l-2 pl-5" style={{ borderColor: PURPLE }}>
              <p className="font-display text-4xl md:text-5xl" style={{ color: PURPLE }}>{v}</p>
              <p className="text-sm text-[#666666] mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories + Sectors */}
      <section className="section-pad py-8">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-3xl mb-5">Award categories</h2>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => <span key={c} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm text-white" style={{ background: PURPLE }}><Award size={14} style={{ color: GOLD }} /> {c}</span>)}
            </div>
          </div>
          <div>
            <h2 className="font-display text-3xl mb-5">Sectors honoured</h2>
            <div className="flex flex-wrap gap-2">
              {SECTORS.map((s) => <span key={s} className="bg-[#f4f4f4] px-3.5 py-2 rounded-full text-sm" style={{ border: `1px solid ${PURPLE}33` }}>{s}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Jury */}
      <section className="section-pad py-14">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex items-center gap-2 mb-8"><Gavel size={20} style={{ color: PURPLE }} /><h2 className="font-display text-3xl md:text-4xl">The jury</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {jury.map((j, i) => (
              <Reveal key={j[0]} delay={(i % 5) * 50}>
                <div className="bg-[#f4f4f4] rounded-2xl p-5 h-full border-t-4" style={{ borderColor: j[1] === 'Jury Chair' ? GOLD : PURPLE }}>
                  <p className="font-display text-lg leading-tight">{j[0]}</p>
                  <p className="text-xs uppercase tracking-wide mt-1" style={{ color: j[1] === 'Jury Chair' ? '#9a7a1e' : '#666666' }}>{j[1]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Past honourees */}
      <section className="section-pad py-8">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex items-center gap-2 mb-8"><Star size={20} style={{ color: GOLD }} /><h2 className="font-display text-3xl md:text-4xl">Past honourees</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {honourees.map((h, i) => (
              <Reveal key={i} delay={(i % 4) * 60}>
                <div className="rounded-2xl p-6 h-full text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${DEEP})` }}>
                  <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: GOLD, color: DEEP }}><Trophy size={20} /></span>
                  <p className="font-display text-xl mt-4">{h[0]}</p>
                  {h[1] ? <p className="text-sm text-white/70">{h[1]}</p> : null}
                  <p className="text-xs uppercase tracking-wide mt-2" style={{ color: GOLD }}>{h[2]}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-xs text-[#666666] mt-4">Representative honourees from the last edition. Full list in the post-show report.</p>
        </div>
      </section>

      <ReportViewer url={eventsData?.felas?.reportPdf || REPORT} accent={GOLD} bg={`linear-gradient(135deg, ${DEEP}, ${PURPLE})`} testid="felas-report"
        eyebrow={eventsData?.felas?.reportEdition || 'Edition 1 · March 2026'} title="Post-show report"
        nextAt={eventsData?.felas?.nextEditionAt} nextLabel={eventsData?.felas?.nextEditionLabel || 'Edition 2 begins in'}
        intro="Flip through the FELAS awards night — winners, jury, sectors and highlights from the edition." />

      {/* Post-show report */}
      <section id="report" className="section-pad py-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-3xl p-10 text-white" style={{ background: `linear-gradient(135deg, ${DEEP}, ${PURPLE})` }}>
              <Quote size={36} style={{ color: GOLD }} />
              <p className="font-display text-2xl md:text-3xl mt-3 leading-snug">Relive the biggest night in energy.</p>
              <p className="text-white/75 mt-3">Download the official FELAS post-show report — winners, jury, sectors and highlights from the last edition.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <LeadForm type="FELAS Post-show Report Download" recipient="vandana@enersider.com" accent={PURPLE}
              heading="Download the post-show report" cta="Get the report" downloadUrl={REPORT}
              extraFields={[{ k: 'interest', l: 'Also interested in (nomination / sponsorship)', full: true }]} />
          </Reveal>
        </div>
      </section>

      {/* Nominate */}
      <section id="nominate" className="section-pad py-10">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div>
              <p className="eyebrow mb-3" style={{ color: PURPLE }}>Nominations open</p>
              <h2 className="font-display text-4xl md:text-5xl">Submit a nomination</h2>
              <p className="text-[#666666] mt-4 max-w-md">Nominate an individual, project or organisation. Choose the award category and sector separately — our jury reviews every submission.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <LeadForm type="FELAS Nomination" recipient="vandana@enersider.com" accent={PURPLE}
              extraFields={[
                { k: 'nominee', l: 'Nominee (person / company / project)', full: true, req: true },
                { k: 'category', l: 'Award category', options: CATEGORIES, req: true },
                { k: 'sector', l: 'Sector', options: SECTORS, req: true },
                { k: 'message', l: 'Why they deserve it', area: true, full: true },
              ]}
              cta="Submit nomination" />
          </Reveal>
        </div>
      </section>

      <section id="partnership" className="section-pad py-14 scroll-mt-28" data-testid="felas-partnership-section">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow mb-3" style={{ color: PURPLE }}>Partner with FELAS</p>
            <h2 className="font-display text-4xl md:text-5xl">Partnership enquiry</h2>
            <p className="text-[#666666] mt-4 max-w-md">Align your brand with India's biggest energy-sector award show. Tell us what you have in mind and our partnerships team will get back to you.</p>
          </div>
          <LeadForm type="FELAS Partnership Enquiry" recipient="partnerships@enersider.com" accent={PURPLE} cta="Send partnership enquiry"
            extraFields={[
              { k: 'designation', l: 'Designation' },
              { k: 'partnership', l: 'Partnership interest', options: ['Title / Presenting partner', 'Award category partner', 'Associate partner', 'Media partner', 'Other'], req: true },
              { k: 'message', l: 'Message', area: true, full: true },
            ]} />
        </div>
      </section>
    </div>
  );
}
